import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { Box, Link, CircularProgress } from '@mui/material';
import { useAuth } from '@/contexts/AuthContext';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import type { LoginCredentials } from '@/types/auth';
import { styled } from '@mui/material/styles';
import { Button, Typography, Input } from '@/components/ui';
import LoginSuccessOverlay from './LoginSuccessOverlay';
import { toast } from 'react-hot-toast';
import { margin } from '@mui/system';

// Add custom CSS for toast loading image
const customToastStyles = `
  .custom-loading-toast .Toastify__spinner {
    display: none !important;
  }
  .custom-loading-toast svg {
    display: none !important;
  }
`;

const BRAND_COLOR = '#17A6DA';

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = customToastStyles;
  document.head.appendChild(styleElement);
}

// Styled components matching Regions design
const LoginContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  height: '100%',
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const LogoHeader = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(3),
  left: theme.spacing(4),
  // [theme.breakpoints.down('sm')]: {
  //   left: '50%',
  //   transform: 'translateX(-50%)',
  // },
}));

const LeftPanel = styled(Box)(({ theme }) => ({
  flex: '0 0 50%',
  padding: theme.spacing(2, 6),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: 'white',
  [theme.breakpoints.down('md')]: {
    flex: '1',
    padding: theme.spacing(4, 3),
    margin: 'auto',
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  flex: '0 0 50%',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    display: 'none', // ✅ hide completely
  },
}));

// Login Banner styled components
const BannerContainer = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  //background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
}));

const BannerImage = styled('img')(() => ({
  width: '100%',
  height: '100%',
  objectPosition: 'cover',
}));

const BrandHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(4),
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    textAlign: 'center',
  },
}));

const WelcomeSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const FormSection = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: '100%',
}));

const FormCard = styled(Box)(({ theme }) => ({
  background: '#ffffff',
  padding: theme.spacing(6, 4),
  borderRadius: '12px',
  boxShadow: `
  0 8px 16px rgba(0,0,0,0.08),
  0 20px 40px rgba(0,0,0,0.15)
`,
  border: '1px solid #f0f0f0',
}));

const inputSx = {
  '& .MuiInputBase-root': {
    backgroundColor: 'transparent !important',
  },

  '& .MuiOutlinedInput-root': {
    backgroundColor: 'transparent !important',
    borderRadius: '8px',

    '& fieldset': {
      borderColor: '#d0d5dd',
    },

    '&:hover fieldset': {
      borderColor: '#808080',
    },

    '&.Mui-focused fieldset': {
      borderColor: ' #808080',
      borderWidth: '2px',
    },
  },

  // Remove browser autofill bg
  '& input:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 1000px transparent inset',
    WebkitTextFillColor: '#000',
    transition: 'background-color 9999s ease-in-out 0s',
  },

  // Label colors
  '& .MuiInputLabel-root': {
    color: '#667085',
  },

  '& .MuiInputLabel-root.Mui-focused': {
    color: BRAND_COLOR,
  },
};

const FooterLinks = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));

// Validation schema
const loginSchema = yup.object({
  email: yup
    .string()
    .required('Email ID is required')
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      'Please enter a valid email address'
    ),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
});

interface LocationState {
  from?: {
    pathname: string;
  };
}

// Initial form values
const initialValues: LoginCredentials = {
  email: '',
  password: '',
};

export const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loading } = useAuth();

  const [showPassword, setShowPassword] = useState(false);

  // Get redirect location
  const from = (location.state as LocationState)?.from?.pathname || '/dashboard';

  // Handle form submission
  const handleSubmit = async (
    values: LoginCredentials,
    { setFieldError }: { setFieldError: (field: string, message: string) => void }
  ) => {
    //setIsSubmitting(true);

    const loadingToast = toast.loading('Welcome back! Getting everything ready for you.', {
      position: 'top-center',
      icon: (
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/c/c7/Loading_2.gif?20170503175831"
          alt="loading"
          style={{
            width: '50px', // change size here
            height: '50px',
            objectFit: 'contain',
            marginRight: '8px',
          }}
        />
      ),
      style: {
        marginTop: '40vh',
        transform: 'translateY(-50%)',

        background: '#ffffff',
        color: '#000',
        padding: '18px 24px',
        borderRadius: '12px',
        width: '620px',
        textAlign: 'center',

        borderTop: '4px solid #17A6DA',
        fontSize: '18px',
        fontWeight: 500,
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
      },
    });

    try {
      await login(values);
      toast.dismiss(loadingToast);
      //toast.success('Login successful!');
    } catch (error) {
      // Dismiss loading toast and show error
      toast.dismiss(loadingToast);

      const errorMessage = error instanceof Error ? error.message : 'SignIn failed';
      toast.error(errorMessage, {
        position: 'top-center',
        style: {
          marginTop: '50vh',
          transform: 'translateY(-50%)',
        },
      });
      setFieldError('email', errorMessage);
    }
  };

  const isLoading = loading;

  return (
    <LoginContainer>
      <LogoHeader>
        <img
          src="/logos/logo-white_1.png"
          alt="GrowthBnk Logo"
          style={{ width: '150px', objectFit: 'contain' }}
        />
      </LogoHeader>

      {/* Left Panel - Login Form */}
      <LeftPanel>
        {/* Brand Header - Regions Style */}
        <FormSection>
          {/* Welcome Section - Regions Style */}
          <WelcomeSection>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'center', sm: 'flex-center' },
                paddingBottom: 1,
              }}
            ></Box>
            <Typography
              variant="h1"
              weight="bold"
              textAlign={'center'}
              sx={{ fontSize: { xs: '1.00rem', sm: '1.5rem' }, color: 'rgb(40, 42, 48)' }}
            >
              Welcome Back
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              textAlign={'center'}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}
            >
              Sign in to your GrowthBnk account.
            </Typography>
          </WelcomeSection>

          {/* Login Form */}
          <Box
            sx={{
              width: {
                xs: '320px', // Mobile devices
                sm: '420px', // Tablets
                md: '700px', // Desktops
                lg: '80%', // Large screens: full width
              },
              maxWidth: '100%',
              margin: '0 auto',
            }}
          >
            <FormCard>
              <Formik
                initialValues={initialValues}
                validationSchema={loginSchema}
                onSubmit={handleSubmit}
              >
                {({ values, errors, touched, setFieldValue, setFieldTouched }) => (
                  <Form>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                      {/* Email ID Field */}
                      <Box>
                        <Input
                          fullWidth
                          label="Email ID"
                          name="email"
                          type="text"
                          placeholder="Enter Email ID"
                          value={values.email}
                          onChange={e => setFieldValue('email', e.target.value)}
                          onBlur={() => setFieldTouched('email', true)}
                          onFocus={() => {
                            // Mark as touched when user focuses on the field
                            if (!touched.email) {
                              setFieldTouched('email', true);
                            }
                          }}
                          error={touched.email && !!errors.email}
                          helperText={touched.email && errors.email}
                          disabled={isLoading}
                          size="small"
                          variant="outlined"
                          sx={inputSx}
                        />
                      </Box>

                      {/* Password Field */}
                      <Box>
                        <Input
                          fullWidth
                          label="Password"
                          name="password"
                          type="password"
                          placeholder="Enter Password"
                          value={values.password}
                          onChange={e => setFieldValue('password', e.target.value)}
                          onBlur={() => setFieldTouched('password', true)}
                          onFocus={() => {
                            // Mark as touched when user focuses on the field
                            if (!touched.password) {
                              setFieldTouched('password', true);
                            }
                          }}
                          error={touched.password && !!errors.password}
                          helperText={touched.password && errors.password}
                          disabled={isLoading}
                          size="small"
                          variant="outlined"
                          showPassword={showPassword}
                          onPasswordToggle={() => setShowPassword(!showPassword)}
                          sx={inputSx}
                        />
                      </Box>
                      <Link
                        component={RouterLink}
                        to="/auth/forgot-password"
                        variant="body2"
                        sx={{
                          color: 'rgb(40, 42, 48)',
                          textDecoration: 'none',
                          fontSize: '0.75rem',
                          textAlign: 'right',
                          fontWeight: '700',
                          '&:hover': {
                            textDecoration: 'none',
                          },
                        }}
                      >
                        Forgot Password?
                      </Link>
                      {/* Login Button */}
                      {/* <Button
                      type="submit"
                      variant="primary"
                      size="small"
                      fullWidth
                      loading={loading}
                      loadingText="Signing in..."
                      disabled={!values.email || !values.password || loading}
                      
                    // onClick={(e) => {
                    //   // Test overlay on button click
                    //   if (values.email && values.password) {
                    //     e.preventDefault();
                    //     console.log('Testing overlay from login button...');
                    //     setShowSuccessOverlay(true);
                    //   }
                    // }}
                    >
                      SignIn
                    </Button> */}
                      <Button
                        type="submit"
                        size="small"
                        fullWidth
                        loading={loading}
                        loadingText="Signing in..."
                        disabled={!values.email || !values.password || loading}
                        sx={{
                          backgroundColor: BRAND_COLOR,
                          color: '#fff',
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: '8px',
                          height: '50px',
                          '&:hover': {
                            backgroundColor: '#1392C1', // slightly darker
                          },
                          '&:disabled': {
                            backgroundColor: '#9bd3ea',
                            color: '#fff',
                          },
                        }}
                      >
                        Sign In
                      </Button>
                    </Box>
                  </Form>
                )}
              </Formik>
            </FormCard>
          </Box>

          {/* Footer Links */}
          <FooterLinks>
            <Link
              component={RouterLink}
              to="/auth/register"
              variant="body2"
              sx={{
                color: 'rgb(40, 42, 48)',
                textDecoration: 'none',
                fontSize: '0.75rem',
                fontWeight: '700',
                '&:hover': {
                  textDecoration: 'none',
                },
              }}
            >
              Merchant SignUp?
            </Link>
          </FooterLinks>
        </FormSection>
      </LeftPanel>

      {/* Right Panel - Login Banner */}
      <RightPanel>
        <BannerContainer>
          <BannerImage src="/banner.svg" alt="Login Banner - Your Dream Home" />
        </BannerContainer>
      </RightPanel>
    </LoginContainer>
  );
};

export default LoginForm;
